import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { formatMediumDate } from "@shared/utilities";
import i18next from "i18next";

export interface FilterDisplayContext {
  rankToTierNameMap?: Record<number, string>;
  rewardsProgramIdToDatesMap?: Record<string, { startDate: string; endDate: string }>;
  subwalletIdToNameMap?: Record<string, string>;
}

type FilterFormatter = (
  value: string | boolean | number | string[] | number[] | null,
  options: FilterOptions,
  context?: FilterDisplayContext,
) => string;

const filterFormatters: Record<string, FilterFormatter> = {
  rewardsProgramId: (value, _options, context) => {
    if (typeof value !== 'string') return '';
    const programDates = context?.rewardsProgramIdToDatesMap?.[value];
    if (programDates) {
      const startYear = programDates.startDate.substring(0, 4);
      const endYear = programDates.endDate.substring(0, 4);
      return `Has points in rewards program ${startYear}-${endYear}`;
    }
    return `Has points in rewards program ${value}`;
  },

  currentRanks: (value, _options, context) => {
    const ranks = Array.isArray(value) ? value : [value];
    const names = ranks.map((r) => (context?.rankToTierNameMap?.[r as number]) ?? String(r));
    return `Current tier-status is ${names.join(', ')}`;
  },
    
  hasReEarnedCurrentTierStatus: (value) => 
    value === true ? 'Has re-earned tier-status' : 'Has not re-earned tier-status',
    
  minPoints: (value, options) => {
    const maxPointsValue = options.maxPoints;
    if (maxPointsValue !== undefined && maxPointsValue !== null) {
      return `Has points within range ${value} to ${maxPointsValue}`;
    }
    return `Has points within range, minimum ${value}`;
  },
    
  maxPoints: (value, options) => {
    const minPointsValue = options.minPoints;
    if (minPointsValue === undefined || minPointsValue === null) {
      return `Has points within range, maximum ${value}`;
    }
    return '';
  },

  subwalletIds: (value, _options, context) => {
    const ids = Array.isArray(value) ? value : [value];
    const names = ids.map((id) => context?.subwalletIdToNameMap?.[id as string] ?? String(id));
    return `Has points in subwallets: ${names.join(', ')}`;
  },

  pointsExpiringOnDate: (value, options) => {
    if (options.pointsExpiringOnDate && typeof value === 'string') {
      return `Has points expiring on ${formatMediumDate(value, i18next)}`;
    }
    return '';
  },
};

export const formatFilterDisplay = (
  key: keyof FilterOptions,
  value: string | boolean | number | string[] | number[] | null,
  options: FilterOptions,
  context?: FilterDisplayContext,
): string => {
  if ((Array.isArray(value) && value.length === 0) || value === null) {
    return '';
  }

  const formatter = filterFormatters[key];
  if (formatter) {
    return formatter(value, options, context);
  }
  
  return '';
};
